import React, { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { CheckCircle, Calendar, Clock, User, Mail, Phone, Briefcase, MessageSquare, ArrowLeft, Home } from 'lucide-react';

const ThankYouPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const bookingData = location.state?.bookingData;

  // Generate confirmation number
  const confirmationNumber = `LW${Date.now().toString().slice(-8)}`;

  useEffect(() => {
    // If no booking data, redirect to home
    if (!bookingData) {
      navigate('/');
    }
  }, [bookingData, navigate]);

  if (!bookingData) {
    return null;
  }

  // Format date for display
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  // Format time for display
  const formatTime = (timeString) => {
    if (!timeString) return '';
    const [hours, minutes] = timeString.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour % 12 || 12;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  // Service labels map
  const serviceLabels = {
    'hair-care': 'Hair Care',
    'beard-grooming': 'Beard Grooming',
    'face-care': 'Face Care',
    'relaxation': 'Relaxation & Massage'
  };

  return (
    <div className="min-h-screen relative pt-20">
      {/* Background Image with Overlay */}
      <div 
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1585747860715-2ba37e788b70?w=1920&q=80)',
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-br from-black/70 via-black/60 to-amber-900/70"></div>
      </div>
      
      {/* Content */}
      <div className="relative z-10 w-full px-4 sm:px-6 lg:px-8 py-16 max-w-4xl mx-auto">
        {/* Success Icon and Message */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-green-500 rounded-full mb-4 animate-bounce shadow-lg">
            <CheckCircle className="w-12 h-12 text-white" />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-3 drop-shadow-lg">
            Thank You!
          </h1>
          <p className="text-xl text-amber-200">
            Your appointment request has been received
          </p>
        </div>

        {/* Confirmation Number */}
        <div className="bg-amber-500 text-black text-center py-4 rounded-t-lg">
          <p className="text-sm font-semibold uppercase tracking-wider mb-1">Confirmation Number</p>
          <p className="text-2xl font-bold tracking-widest">{confirmationNumber}</p>
        </div>

        {/* Booking Details Card */}
        <div className="bg-white rounded-b-lg shadow-2xl p-8 mb-8">
          <h2 className="text-2xl font-bold text-black mb-6 border-b border-gray-200 pb-3">
            Booking Details
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Name */}
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0">
                <User className="w-5 h-5 text-amber-600" />
              </div>
              <div className="flex-1">
                <p className="text-sm text-gray-500 font-medium">Name</p>
                <p className="text-lg text-black font-semibold">{bookingData.name}</p>
              </div>
            </div>

            {/* Email */}
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0">
                <Mail className="w-5 h-5 text-amber-600" />
              </div>
              <div className="flex-1">
                <p className="text-sm text-gray-500 font-medium">Email</p>
                <p className="text-lg text-black">{bookingData.email}</p>
              </div>
            </div>

            {/* Phone */}
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0">
                <Phone className="w-5 h-5 text-amber-600" />
              </div>
              <div className="flex-1">
                <p className="text-sm text-gray-500 font-medium">Contact Number</p>
                <p className="text-lg text-black">{bookingData.countryCode} {bookingData.phone}</p>
              </div>
            </div>

            {/* Service */}
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0">
                <Briefcase className="w-5 h-5 text-amber-600" />
              </div>
              <div className="flex-1">
                <p className="text-sm text-gray-500 font-medium">Service</p>
                <p className="text-lg text-black font-semibold">{serviceLabels[bookingData.service] || bookingData.service}</p>
              </div>
            </div>

            {/* Date */}
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0">
                <Calendar className="w-5 h-5 text-amber-600" />
              </div>
              <div className="flex-1">
                <p className="text-sm text-gray-500 font-medium">Date</p>
                <p className="text-lg text-black font-semibold">{formatDate(bookingData.date)}</p>
              </div>
            </div>

            {/* Time */}
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0">
                <Clock className="w-5 h-5 text-amber-600" />
              </div>
              <div className="flex-1">
                <p className="text-sm text-gray-500 font-medium">Time</p>
                <p className="text-lg text-black font-semibold">{formatTime(bookingData.time)}</p>
              </div>
            </div>

            {/* Additional Message - Full Width */}
            {bookingData.description && (
              <div className="flex items-start gap-4 md:col-span-2">
                <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <MessageSquare className="w-5 h-5 text-amber-600" />
                </div>
                <div className="flex-1">
                  <p className="text-sm text-gray-500 font-medium">Additional Message</p>
                  <p className="text-base text-gray-700 mt-1">{bookingData.description}</p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* What's Next Section */}
        <div className="bg-black text-white rounded-lg p-6 mb-8">
          <h3 className="text-xl font-bold mb-4">What Happens Next?</h3>
          <ul className="space-y-3">
            <li className="flex items-start gap-3">
              <span className="text-amber-500 font-bold">1.</span>
              <span>Our team will review your appointment request</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="text-amber-500 font-bold">2.</span>
              <span>We'll contact you within 24 hours to confirm availability</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="text-amber-500 font-bold">3.</span>
              <span>You'll receive a confirmation email with final details</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="text-amber-500 font-bold">4.</span>
              <span>Save your confirmation number: <strong className="text-amber-500">{confirmationNumber}</strong></span>
            </li>
          </ul>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <button
            onClick={() => navigate('/')}
            className="inline-flex items-center justify-center gap-2 bg-amber-500 text-black px-8 py-4 font-bold uppercase tracking-wider hover:bg-amber-600 transition-colors"
          >
            <Home size={20} />
            Back to Home
          </button>
          <button
            onClick={() => navigate('/', { state: { openBooking: true } })}
            className="inline-flex items-center justify-center gap-2 bg-white text-black border-2 border-black px-8 py-4 font-bold uppercase tracking-wider hover:bg-gray-100 transition-colors"
          >
            <Calendar size={20} />
            Book Another
          </button>
        </div>

        {/* Contact Info */}
        <div className="text-center mt-12 pt-8 border-t border-white/20">
          <p className="text-white mb-2 text-lg">Need to make changes or have questions?</p>
          <p className="text-white font-semibold text-lg">Call us at <a href="tel:+971564740785" className="text-amber-400 hover:text-amber-300 hover:underline transition-colors">+971 564 740 785</a></p>
          <p className="text-white mt-1 text-lg">or email <a href="mailto:inquiry@lookwellgentssalon.com" className="text-amber-400 hover:text-amber-300 hover:underline transition-colors">inquiry@lookwellgentssalon.com</a></p>
        </div>
      </div>
    </div>
  );
};

export default ThankYouPage;
